import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Plus, Sparkles, Trash2 } from 'lucide-react'
import { adminApi } from '../api'
import ImageUpload from '../ImageUpload'
import { IMAGE_SIZE_HINTS } from '../media'
import RichTextEditor from '../editor/RichTextEditor'
import {
  SCHEMA_TYPES,
  SEARCH_INTENTS,
  SEO_LIMITS,
  analyzeContent,
  buildSeoChecks,
  suggestAiSummary,
  suggestFaqs,
  suggestHowToSteps,
  suggestKeywords,
  suggestMetaDescription,
  suggestRelatedQueries,
  suggestSecondaryKeywords,
  suggestSeoTitle,
  suggestTakeaways,
  type FaqItem,
  type HowToStep,
} from '../editor/localSeoAssist'
import { Btn, Card, Field, FormSkeleton, areaClass, inputClass } from '../ui'
import type { CaseStudyDetail } from './caseStudiesShared'

type FormState = {
  title: string
  slug: string
  summary: string
  content_html: string
  is_published: boolean
  is_active: boolean
  published_at: string
  client_name: string
  industry: string
  sort_order: string
  conversion_rate: string
  average_ticket_size: string
  total_site_visits: string
  campaign_duration: string
  active_pipeline_value: string
  services_delivered: string
  cover_image_path: string | null
  cover_stored_path: string | null
  seo_title: string
  seo_description: string
  seo_keywords: string
  focus_keyword: string
  secondary_keywords: string
  canonical_path: string
  og_title: string
  og_description: string
  schema_type: string
  noindex: boolean
  nofollow: boolean
  exclude_from_sitemap: boolean
  ai_summary: string
  key_takeaways: string
  faq: FaqItem[]
  how_to: HowToStep[]
  related_queries: string
  search_intent: string
  author_name: string
  author_role: string
  target_locations: string
  primary_city: string
  service_area: string
  breadcrumb_label: string
  reviewed_at: string
}

const empty = (): FormState => ({
  title: '',
  slug: '',
  summary: '',
  content_html: '',
  is_published: false,
  is_active: true,
  published_at: new Date().toISOString().slice(0, 16),
  client_name: '',
  industry: '',
  sort_order: '0',
  conversion_rate: '',
  average_ticket_size: '',
  total_site_visits: '',
  campaign_duration: '',
  active_pipeline_value: '',
  services_delivered: '',
  cover_image_path: null,
  cover_stored_path: null,
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  focus_keyword: '',
  secondary_keywords: '',
  canonical_path: '',
  og_title: '',
  og_description: '',
  schema_type: 'CaseStudy',
  noindex: false,
  nofollow: false,
  exclude_from_sitemap: false,
  ai_summary: '',
  key_takeaways: '',
  faq: [],
  how_to: [],
  related_queries: '',
  search_intent: 'commercial',
  author_name: 'DigiSutra',
  author_role: 'Digital marketing strategist',
  target_locations: 'Surat, Gujarat, India',
  primary_city: 'Surat',
  service_area: 'Surat, Gujarat, India',
  breadcrumb_label: '',
  reviewed_at: new Date().toISOString().slice(0, 16),
})

function parseFaq(raw: unknown): FaqItem[] {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item) => ({
        q: String(item?.q || item?.question || ''),
        a: String(item?.a || item?.answer || ''),
      }))
      .filter((item) => item.q || item.a)
  } catch {
    return []
  }
}

function parseHowTo(raw: unknown): HowToStep[] {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item, i) => ({
        name: String(item?.name || item?.title || `Step ${i + 1}`),
        text: String(item?.text || item?.description || ''),
      }))
      .filter((item) => item.name || item.text)
  } catch {
    return []
  }
}

function CharCount({ value, max, ideal }: { value: string; max: number; ideal?: [number, number] }) {
  const len = value.length
  const inIdeal = ideal ? len >= ideal[0] && len <= ideal[1] : len > 0 && len <= max
  return (
    <span
      className={`text-[0.7rem] tabular-nums ${len > max ? 'text-red-600' : inIdeal ? 'text-emerald-700' : 'text-slate-400'}`}
    >
      {len}/{max}
      {ideal ? ` · ideal ${ideal[0]}–${ideal[1]}` : ''}
    </span>
  )
}

function slugify(raw: string) {
  return raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 180)
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-primary pt-2 border-t border-primary/10">
      {children}
    </p>
  )
}

export default function CaseStudyFormPage() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const [tab, setTab] = useState<'content' | 'seo'>('content')
  const [form, setForm] = useState<FormState>(empty())
  const [slugTouched, setSlugTouched] = useState(false)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [assistNote, setAssistNote] = useState<string | null>(null)

  useEffect(() => {
    if (isNew) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const { res, data } = await adminApi<{ caseStudy?: CaseStudyDetail }>(`/api/admin/website/case-studies/${id}`)
      if (cancelled) return
      const cs = data.caseStudy
      if (!res.ok || !cs) {
        setErr('Case study not found')
        setLoading(false)
        return
      }
      setForm({
        title: String(cs.title || ''),
        slug: String(cs.slug || ''),
        summary: String(cs.summary || ''),
        content_html: String(cs.content_html || ''),
        is_published: !!cs.is_published,
        is_active: cs.is_active === undefined ? true : !!cs.is_active,
        published_at: cs.published_at ? String(cs.published_at).slice(0, 16).replace(' ', 'T') : '',
        client_name: String(cs.client_name || ''),
        industry: String(cs.industry || ''),
        sort_order: String(cs.sort_order ?? 0),
        conversion_rate: String(cs.conversion_rate || ''),
        average_ticket_size: String(cs.average_ticket_size || ''),
        total_site_visits: String(cs.total_site_visits || ''),
        campaign_duration: String(cs.campaign_duration || ''),
        active_pipeline_value: String(cs.active_pipeline_value || ''),
        services_delivered: String(cs.services_delivered || ''),
        cover_image_path: cs.cover_image_path || null,
        cover_stored_path: cs.cover_stored_path || null,
        seo_title: String(cs.seo_title || ''),
        seo_description: String(cs.seo_description || ''),
        seo_keywords: String(cs.seo_keywords || ''),
        focus_keyword: String(cs.focus_keyword || ''),
        secondary_keywords: String(cs.secondary_keywords || ''),
        canonical_path: String(cs.canonical_path || ''),
        og_title: String(cs.og_title || ''),
        og_description: String(cs.og_description || ''),
        schema_type: String(cs.schema_type || 'CaseStudy'),
        noindex: !!cs.noindex,
        nofollow: !!cs.nofollow,
        exclude_from_sitemap: !!cs.exclude_from_sitemap,
        ai_summary: String(cs.ai_summary || ''),
        key_takeaways: String(cs.key_takeaways || ''),
        faq: parseFaq(cs.faq_json),
        how_to: parseHowTo(cs.how_to_json),
        related_queries: String(cs.related_queries || ''),
        search_intent: String(cs.search_intent || 'commercial'),
        author_name: String(cs.author_name || 'DigiSutra'),
        author_role: String(cs.author_role || ''),
        target_locations: String(cs.target_locations || ''),
        primary_city: String(cs.primary_city || ''),
        service_area: String(cs.service_area || ''),
        breadcrumb_label: String(cs.breadcrumb_label || ''),
        reviewed_at: cs.reviewed_at ? String(cs.reviewed_at).slice(0, 16).replace(' ', 'T') : '',
      })
      setSlugTouched(true)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  const stats = useMemo(
    () => analyzeContent(form.content_html, form.title, form.focus_keyword),
    [form.content_html, form.title, form.focus_keyword]
  )

  const checks = useMemo(
    () =>
      buildSeoChecks({
        title: form.title,
        summary: form.summary,
        html: form.content_html,
        seoTitle: form.seo_title,
        seoDescription: form.seo_description,
        focusKeyword: form.focus_keyword,
        secondaryKeywords: form.secondary_keywords,
        targetLocations: form.target_locations,
        primaryCity: form.primary_city,
        serviceArea: form.service_area,
        keyTakeaways: form.key_takeaways,
        faq: form.faq,
        howTo: form.how_to,
        relatedQueries: form.related_queries,
        aiSummary: form.ai_summary,
        authorName: form.author_name,
        authorRole: form.author_role,
        searchIntent: form.search_intent,
        schemaType: form.schema_type,
        ogTitle: form.og_title,
        reviewedAt: form.reviewed_at,
      }),
    [form]
  )

  const score = useMemo(() => {
    const done = checks.filter((c) => c.ok).length
    return { done, total: checks.length, pct: Math.round((done / checks.length) * 100) }
  }, [checks])

  const previewTitle = useMemo(
    () => (form.seo_title || form.title || 'Page title').slice(0, SEO_LIMITS.titleMax),
    [form.seo_title, form.title]
  )
  const previewDesc = useMemo(
    () =>
      (form.seo_description || form.summary || 'Meta description will appear here.').slice(
        0,
        SEO_LIMITS.metaMax
      ),
    [form.seo_description, form.summary]
  )
  const previewUrl = useMemo(() => {
    const path = form.canonical_path || (form.slug ? `/case-study/${form.slug}` : '/case-study/…')
    return `https://digisutra.in${path.startsWith('/') ? path : `/${path}`}`
  }, [form.canonical_path, form.slug])

  function flash(msg: string) {
    setAssistNote(msg)
    window.setTimeout(() => setAssistNote(null), 2800)
  }

  function applyAllLocalAssist() {
    const loc = form.target_locations || form.primary_city
    const seedTags = [form.client_name, form.industry, form.services_delivered]
      .filter(Boolean)
      .join(', ')
    const seo_title = suggestSeoTitle(form.title, form.focus_keyword, loc)
    const seo_description = suggestMetaDescription(
      form.summary,
      form.content_html,
      form.focus_keyword,
      loc
    )
    const seo_keywords = suggestKeywords(
      form.focus_keyword,
      seedTags,
      loc,
      form.title,
      form.content_html
    )
    setForm((prev) => ({
      ...prev,
      seo_title,
      seo_description,
      seo_keywords,
      secondary_keywords: suggestSecondaryKeywords(
        prev.focus_keyword,
        seedTags,
        loc,
        prev.title,
        prev.content_html
      ),
      og_title: prev.og_title || seo_title,
      og_description: prev.og_description || seo_description,
      ai_summary: suggestAiSummary(prev.summary, prev.content_html, prev.focus_keyword, loc),
      key_takeaways: suggestTakeaways(prev.content_html, prev.title, prev.focus_keyword),
      faq: suggestFaqs(prev.content_html, prev.title, prev.focus_keyword, loc),
      how_to: suggestHowToSteps(prev.content_html, prev.title, prev.focus_keyword),
      related_queries: suggestRelatedQueries(prev.title, prev.focus_keyword, loc, prev.content_html),
      canonical_path: prev.canonical_path || (prev.slug ? `/case-study/${prev.slug}` : ''),
      breadcrumb_label: prev.breadcrumb_label || prev.title.slice(0, 60),
      primary_city: prev.primary_city || loc.split(',')[0]?.trim() || 'Surat',
      service_area: prev.service_area || loc,
      summary: prev.summary.trim() || seo_description.slice(0, 220),
      reviewed_at: prev.reviewed_at || new Date().toISOString().slice(0, 16),
      schema_type: prev.schema_type || 'CaseStudy',
      search_intent: prev.search_intent || 'commercial',
    }))
    setTab('seo')
    flash('Local assist filled SEO · AEO · GEO fields from your content')
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSaving(true)
    const fd = new FormData()
    fd.set('title', form.title)
    fd.set('slug', form.slug)
    fd.set('summary', form.summary)
    fd.set('content_html', form.content_html)
    fd.set('is_published', form.is_published ? '1' : '0')
    fd.set('is_active', form.is_active ? '1' : '0')
    fd.set(
      'published_at',
      form.published_at ? new Date(form.published_at).toISOString().slice(0, 19).replace('T', ' ') : ''
    )
    fd.set('client_name', form.client_name)
    fd.set('industry', form.industry)
    fd.set('sort_order', form.sort_order)
    fd.set('conversion_rate', form.conversion_rate)
    fd.set('average_ticket_size', form.average_ticket_size)
    fd.set('total_site_visits', form.total_site_visits)
    fd.set('campaign_duration', form.campaign_duration)
    fd.set('active_pipeline_value', form.active_pipeline_value)
    fd.set('services_delivered', form.services_delivered)
    fd.set('seo_title', form.seo_title)
    fd.set('seo_description', form.seo_description)
    fd.set('seo_keywords', form.seo_keywords)
    fd.set('focus_keyword', form.focus_keyword)
    fd.set('secondary_keywords', form.secondary_keywords)
    fd.set('canonical_path', form.canonical_path)
    fd.set('og_title', form.og_title)
    fd.set('og_description', form.og_description)
    fd.set('og_image_path', form.cover_stored_path || form.cover_image_path || '')
    fd.set('schema_type', form.schema_type)
    fd.set('noindex', form.noindex ? '1' : '0')
    fd.set('nofollow', form.nofollow ? '1' : '0')
    fd.set('exclude_from_sitemap', form.exclude_from_sitemap ? '1' : '0')
    fd.set('ai_summary', form.ai_summary)
    fd.set('key_takeaways', form.key_takeaways)
    fd.set('faq_json', JSON.stringify(form.faq.filter((f) => f.q.trim() && f.a.trim())))
    fd.set(
      'how_to_json',
      JSON.stringify(form.how_to.filter((s) => s.name.trim() && s.text.trim()))
    )
    fd.set('related_queries', form.related_queries)
    fd.set('search_intent', form.search_intent)
    fd.set('author_name', form.author_name)
    fd.set('author_role', form.author_role)
    fd.set('target_locations', form.target_locations)
    fd.set('primary_city', form.primary_city)
    fd.set('service_area', form.service_area)
    fd.set('breadcrumb_label', form.breadcrumb_label)
    fd.set(
      'reviewed_at',
      form.reviewed_at ? new Date(form.reviewed_at).toISOString().slice(0, 19).replace('T', ' ') : ''
    )
    fd.set('cover_image_path', form.cover_stored_path || form.cover_image_path || '')

    const path = isNew ? '/api/admin/website/case-studies' : `/api/admin/website/case-studies/${id}`
    const { res, data } = await adminApi<{ message?: string; id?: number }>(path, {
      method: isNew ? 'POST' : 'PUT',
      formData: fd,
    })
    setSaving(false)
    if (!res.ok) {
      setErr(data.message || 'Save failed')
      return
    }
    const nextId = isNew ? data.id : Number(id)
    if (nextId) navigate(`/admin/case-studies/${nextId}`, { replace: true })
    else navigate('/admin/case-studies', { replace: true })
  }

  if (loading) {
    return <FormSkeleton />
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <Btn
          variant="ghost"
          onClick={() => navigate(isNew ? '/admin/case-studies' : `/admin/case-studies/${id}`)}
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Btn>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-2xl border border-primary/15 bg-slate-50/80 p-0.5">
            {(
              [
                ['content', 'Content'],
                ['seo', 'SEO'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`rounded-xl px-3.5 py-2 text-sm font-semibold transition whitespace-nowrap ${
                  tab === key ? 'bg-white text-primary shadow-sm ring-1 ring-primary/15' : 'text-slate-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <Btn variant="ghost" onClick={applyAllLocalAssist}>
            <Sparkles className="h-4 w-4" /> Local assist
          </Btn>
        </div>
      </div>

      {assistNote && (
        <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
          {assistNote}
        </p>
      )}

      <form onSubmit={save} className="grid xl:grid-cols-[minmax(0,1fr)_320px] gap-3 sm:gap-4 items-start">
        <Card className="!p-4 sm:!p-5 space-y-3 sm:space-y-4">
          {tab === 'content' ? (
            <>
              <div className="grid sm:grid-cols-[1.4fr_1fr] gap-3">
                <Field label="Title">
                  <input
                    className={inputClass}
                    value={form.title}
                    onChange={(e) => {
                      const title = e.target.value
                      setForm((prev) => ({
                        ...prev,
                        title,
                        slug: slugTouched ? prev.slug : slugify(title),
                      }))
                    }}
                    required
                  />
                </Field>
                <Field label="Slug">
                  <input
                    className={inputClass}
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true)
                      setForm({ ...form, slug: slugify(e.target.value) || e.target.value })
                    }}
                    placeholder="auto from title"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Client">
                  <input
                    className={inputClass}
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    placeholder="Client / brand name"
                  />
                </Field>
                <Field label="Industry">
                  <input
                    className={inputClass}
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    placeholder="e.g. Real estate, B2B SaaS"
                  />
                </Field>
              </div>
              <Field label="Summary">
                <textarea
                  className={areaClass}
                  style={{ minHeight: 72 }}
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder="Card excerpt + meta fallback"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Conversion rate">
                  <input
                    className={inputClass}
                    value={form.conversion_rate}
                    onChange={(e) => setForm({ ...form, conversion_rate: e.target.value })}
                    placeholder="e.g. 4.8%"
                  />
                </Field>
                <Field label="Avg ticket size">
                  <input
                    className={inputClass}
                    value={form.average_ticket_size}
                    onChange={(e) => setForm({ ...form, average_ticket_size: e.target.value })}
                    placeholder="e.g. ₹45,000"
                  />
                </Field>
                <Field label="Site visits">
                  <input
                    className={inputClass}
                    value={form.total_site_visits}
                    onChange={(e) => setForm({ ...form, total_site_visits: e.target.value })}
                    placeholder="e.g. 1.2L"
                  />
                </Field>
                <Field label="Campaign duration">
                  <input
                    className={inputClass}
                    value={form.campaign_duration}
                    onChange={(e) => setForm({ ...form, campaign_duration: e.target.value })}
                    placeholder="e.g. 6 months"
                  />
                </Field>
                <Field label="Pipeline value">
                  <input
                    className={inputClass}
                    value={form.active_pipeline_value}
                    onChange={(e) => setForm({ ...form, active_pipeline_value: e.target.value })}
                    placeholder="e.g. ₹2.4 Cr"
                  />
                </Field>
                <Field label="Services delivered">
                  <input
                    className={inputClass}
                    value={form.services_delivered}
                    onChange={(e) => setForm({ ...form, services_delivered: e.target.value })}
                    placeholder="Meta Ads, SEO, CRM…"
                  />
                </Field>
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-700">Content</span>
                  <span className="text-xs text-slate-500 tabular-nums">
                    {stats.wordCount} words · {stats.h2s.length} H2s · {stats.keywordDensity.toFixed(1)}%
                  </span>
                </div>
                <RichTextEditor
                  value={form.content_html}
                  onChange={(html) => setForm((prev) => ({ ...prev, content_html: html }))}
                  minHeight={420}
                />
              </div>
            </>
          ) : (
            <>
              <div className="rounded-2xl border border-primary/10 bg-primary/[0.03] p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-primary">
                    SEO
                  </p>
                  <p className="text-sm text-slate-600">
                    Title ≤{SEO_LIMITS.titleMax} · meta ≤{SEO_LIMITS.metaMax} · AI summary ≤
                    {SEO_LIMITS.aiSummaryMax} · body ≥{SEO_LIMITS.minWords} words
                  </p>
                </div>
                <Btn variant="ghost" onClick={applyAllLocalAssist}>
                  <Sparkles className="h-4 w-4" /> Fill all
                </Btn>
              </div>

              <SectionLabel>Classic SEO</SectionLabel>
              <Field label="Focus keyword">
                <input
                  className={inputClass}
                  value={form.focus_keyword}
                  onChange={(e) => setForm({ ...form, focus_keyword: e.target.value })}
                  placeholder="e.g. lead generation agency Surat"
                />
              </Field>
              <Field label="Secondary keywords">
                <input
                  className={inputClass}
                  value={form.secondary_keywords}
                  onChange={(e) => setForm({ ...form, secondary_keywords: e.target.value })}
                  placeholder="meta ads, b2b leads, funnel"
                />
              </Field>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-700">SEO title</span>
                  <CharCount value={form.seo_title} max={SEO_LIMITS.titleMax} ideal={SEO_LIMITS.titleIdeal} />
                </div>
                <input
                  className={inputClass}
                  value={form.seo_title}
                  onChange={(e) =>
                    setForm({ ...form, seo_title: e.target.value.slice(0, SEO_LIMITS.titleMax) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-700">Meta description</span>
                  <CharCount
                    value={form.seo_description}
                    max={SEO_LIMITS.metaMax}
                    ideal={SEO_LIMITS.metaIdeal}
                  />
                </div>
                <textarea
                  className={areaClass}
                  style={{ minHeight: 90 }}
                  value={form.seo_description}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      seo_description: e.target.value.slice(0, SEO_LIMITS.metaMax),
                    })
                  }
                />
              </div>
              <Field label="SEO keywords">
                <input
                  className={inputClass}
                  value={form.seo_keywords}
                  onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })}
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Canonical path">
                  <input
                    className={inputClass}
                    value={form.canonical_path}
                    onChange={(e) => setForm({ ...form, canonical_path: e.target.value })}
                    placeholder="/case-study/your-slug"
                  />
                </Field>
                <Field label="Breadcrumb label">
                  <input
                    className={inputClass}
                    value={form.breadcrumb_label}
                    onChange={(e) => setForm({ ...form, breadcrumb_label: e.target.value })}
                    placeholder="Short nav label"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Schema type">
                  <select
                    className={inputClass}
                    value={form.schema_type}
                    onChange={(e) => setForm({ ...form, schema_type: e.target.value })}
                  >
                    {SCHEMA_TYPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Search intent">
                  <select
                    className={inputClass}
                    value={form.search_intent}
                    onChange={(e) => setForm({ ...form, search_intent: e.target.value })}
                  >
                    {SEARCH_INTENTS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <SectionLabel>Social (Open Graph)</SectionLabel>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-700">OG title</span>
                  <CharCount value={form.og_title} max={SEO_LIMITS.ogTitleMax} />
                </div>
                <input
                  className={inputClass}
                  value={form.og_title}
                  onChange={(e) =>
                    setForm({ ...form, og_title: e.target.value.slice(0, SEO_LIMITS.ogTitleMax) })
                  }
                  placeholder="Falls back to SEO title"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-700">OG description</span>
                  <CharCount value={form.og_description} max={SEO_LIMITS.ogDescMax} />
                </div>
                <textarea
                  className={areaClass}
                  style={{ minHeight: 70 }}
                  value={form.og_description}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      og_description: e.target.value.slice(0, SEO_LIMITS.ogDescMax),
                    })
                  }
                  placeholder="Falls back to meta description"
                />
              </div>

              <SectionLabel>AEO — answer engines</SectionLabel>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    Direct answer / AI summary
                  </span>
                  <CharCount
                    value={form.ai_summary}
                    max={SEO_LIMITS.aiSummaryMax}
                    ideal={SEO_LIMITS.aiSummaryIdeal}
                  />
                </div>
                <textarea
                  className={areaClass}
                  style={{ minHeight: 80 }}
                  value={form.ai_summary}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ai_summary: e.target.value.slice(0, SEO_LIMITS.aiSummaryMax),
                    })
                  }
                  placeholder="One clear paragraph an AI / featured snippet can quote"
                />
              </div>
              <Field label="Key takeaways (one per line)">
                <textarea
                  className={areaClass}
                  style={{ minHeight: 90 }}
                  value={form.key_takeaways}
                  onChange={(e) => setForm({ ...form, key_takeaways: e.target.value })}
                />
              </Field>
              <Field label="Related queries / People Also Ask (one per line)">
                <textarea
                  className={areaClass}
                  style={{ minHeight: 90 }}
                  value={form.related_queries}
                  onChange={(e) => setForm({ ...form, related_queries: e.target.value })}
                />
              </Field>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-700">FAQ schema</p>
                    <p className="text-xs text-slate-500">Rich results + AEO</p>
                  </div>
                  <Btn
                    variant="ghost"
                    onClick={() => setForm({ ...form, faq: [...form.faq, { q: '', a: '' }] })}
                  >
                    <Plus className="h-4 w-4" /> Add
                  </Btn>
                </div>
                {form.faq.map((item, idx) => (
                  <div key={idx} className="rounded-2xl border border-primary/10 bg-slate-50/50 p-3 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs font-bold uppercase text-slate-500">FAQ {idx + 1}</span>
                      <button
                        type="button"
                        className="text-red-600 p-1.5 rounded-lg hover:bg-red-50"
                        onClick={() => setForm({ ...form, faq: form.faq.filter((_, i) => i !== idx) })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <input
                      className={inputClass}
                      placeholder="Question"
                      value={item.q}
                      onChange={(e) => {
                        const faq = [...form.faq]
                        faq[idx] = { ...faq[idx], q: e.target.value }
                        setForm({ ...form, faq })
                      }}
                    />
                    <textarea
                      className={areaClass}
                      style={{ minHeight: 60 }}
                      placeholder="Answer"
                      value={item.a}
                      onChange={(e) => {
                        const faq = [...form.faq]
                        faq[idx] = { ...faq[idx], a: e.target.value }
                        setForm({ ...form, faq })
                      }}
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-700">How-to steps</p>
                    <p className="text-xs text-slate-500">Required if schema = HowTo</p>
                  </div>
                  <Btn
                    variant="ghost"
                    onClick={() =>
                      setForm({ ...form, how_to: [...form.how_to, { name: '', text: '' }] })
                    }
                  >
                    <Plus className="h-4 w-4" /> Add
                  </Btn>
                </div>
                {form.how_to.map((item, idx) => (
                  <div key={idx} className="rounded-2xl border border-primary/10 bg-slate-50/50 p-3 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs font-bold uppercase text-slate-500">Step {idx + 1}</span>
                      <button
                        type="button"
                        className="text-red-600 p-1.5 rounded-lg hover:bg-red-50"
                        onClick={() =>
                          setForm({ ...form, how_to: form.how_to.filter((_, i) => i !== idx) })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <input
                      className={inputClass}
                      placeholder="Step name"
                      value={item.name}
                      onChange={(e) => {
                        const how_to = [...form.how_to]
                        how_to[idx] = { ...how_to[idx], name: e.target.value }
                        setForm({ ...form, how_to })
                      }}
                    />
                    <textarea
                      className={areaClass}
                      style={{ minHeight: 60 }}
                      placeholder="Step details"
                      value={item.text}
                      onChange={(e) => {
                        const how_to = [...form.how_to]
                        how_to[idx] = { ...how_to[idx], text: e.target.value }
                        setForm({ ...form, how_to })
                      }}
                    />
                  </div>
                ))}
              </div>

              <SectionLabel>GEO · E-E-A-T</SectionLabel>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Author">
                  <input
                    className={inputClass}
                    value={form.author_name}
                    onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                  />
                </Field>
                <Field label="Author role">
                  <input
                    className={inputClass}
                    value={form.author_role}
                    onChange={(e) => setForm({ ...form, author_role: e.target.value })}
                    placeholder="Strategist, Founder…"
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Primary city">
                  <input
                    className={inputClass}
                    value={form.primary_city}
                    onChange={(e) => setForm({ ...form, primary_city: e.target.value })}
                    placeholder="Surat"
                  />
                </Field>
                <Field label="Reviewed at">
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={form.reviewed_at}
                    onChange={(e) => setForm({ ...form, reviewed_at: e.target.value })}
                  />
                </Field>
              </div>
              <Field label="Target locations">
                <input
                  className={inputClass}
                  value={form.target_locations}
                  onChange={(e) => setForm({ ...form, target_locations: e.target.value })}
                  placeholder="Surat, Mumbai, India"
                />
              </Field>
              <Field label="Service area">
                <input
                  className={inputClass}
                  value={form.service_area}
                  onChange={(e) => setForm({ ...form, service_area: e.target.value })}
                  placeholder="Gujarat, India"
                />
              </Field>

              <SectionLabel>Indexing</SectionLabel>
              <div className="flex flex-col gap-2">
                <label className="text-sm flex items-center gap-2 font-medium text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-primary/30"
                    checked={form.noindex}
                    onChange={(e) => setForm({ ...form, noindex: e.target.checked })}
                  />
                  Noindex
                </label>
                <label className="text-sm flex items-center gap-2 font-medium text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-primary/30"
                    checked={form.nofollow}
                    onChange={(e) => setForm({ ...form, nofollow: e.target.checked })}
                  />
                  Nofollow
                </label>
                <label className="text-sm flex items-center gap-2 font-medium text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-primary/30"
                    checked={form.exclude_from_sitemap}
                    onChange={(e) => setForm({ ...form, exclude_from_sitemap: e.target.checked })}
                  />
                  Exclude from sitemap
                </label>
              </div>
            </>
          )}

          {err && (
            <p className="text-sm text-red-600 rounded-xl bg-red-50 border border-red-100 px-3 py-2">{err}</p>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-primary/10 xl:hidden">
            <Btn type="submit" disabled={saving}>
              {saving
                ? 'Saving…'
                : form.is_published
                  ? isNew
                    ? 'Publish case study'
                    : 'Save & publish'
                  : isNew
                    ? 'Save draft'
                    : 'Update draft'}
            </Btn>
            <Btn
              variant="ghost"
              onClick={() => navigate(isNew ? '/admin/case-studies' : `/admin/case-studies/${id}`)}
            >
              Cancel
            </Btn>
          </div>
        </Card>

        <aside className="space-y-3 xl:sticky xl:top-20 order-first xl:order-none">
          {/* Publish panel */}
          <Card className="!p-0 overflow-hidden">
            <div
              className={`px-4 py-3 border-b border-primary/10 ${
                form.is_published
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-500'
                  : 'bg-gradient-to-r from-navy-900 to-navy-800'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white/70">
                    Status
                  </p>
                  <p className="text-sm font-bold text-white">
                    {form.is_published ? 'Published' : 'Draft'}
                  </p>
                </div>
                <div className="inline-flex rounded-xl bg-black/20 p-0.5 backdrop-blur">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_published: false })}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      !form.is_published ? 'bg-white text-navy-900 shadow' : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Draft
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        is_published: true,
                        published_at: form.published_at || new Date().toISOString().slice(0, 16),
                      })
                    }
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                      form.is_published ? 'bg-white text-emerald-700 shadow' : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Publish
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <ImageUpload
                label="Cover"
                aspectClass="aspect-video"
                hint={IMAGE_SIZE_HINTS.cover}
                value={form.cover_image_path}
                onChange={({ storedPath, url }) =>
                  setForm({
                    ...form,
                    cover_image_path: url || null,
                    cover_stored_path: storedPath,
                  })
                }
              />

              <label className="text-sm flex items-center gap-2 font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-primary/30"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Active on site
              </label>

              <div className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Sort order</span>
                <input
                  className={inputClass}
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  inputMode="numeric"
                />
              </div>

              <div className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Publish date
                </span>
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={form.published_at}
                  onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                  disabled={!form.is_published}
                />
                {!form.is_published && (
                  <p className="text-[0.7rem] text-slate-400">Enabled when status is Publish</p>
                )}
              </div>

              <div className="rounded-xl bg-slate-50 border border-primary/10 px-3 py-2.5 text-[0.7rem] text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                <span>{stats.wordCount} words</span>
                <span>~{stats.readingMinutes} min</span>
                <span>{score.pct}% SEO</span>
              </div>

              <div className="[&_button]:w-full">
                <Btn type="submit" disabled={saving}>
                  {saving
                    ? 'Saving…'
                    : form.is_published
                      ? isNew
                        ? 'Publish case study'
                        : 'Save & publish'
                      : isNew
                        ? 'Save draft'
                        : 'Update draft'}
                </Btn>
              </div>
            </div>
          </Card>

          {tab === 'seo' && (
            <>
              <Card className="!p-3.5 space-y-2.5">
                <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-slate-500">
                  SERP preview
                </p>
                <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1">
                  <p className="text-[#1a0dab] text-sm leading-snug font-medium line-clamp-2">
                    {previewTitle}
                  </p>
                  <p className="text-[#006621] text-[0.7rem] truncate">{previewUrl}</p>
                  <p className="text-[#4d5156] text-xs leading-snug line-clamp-3">{previewDesc}</p>
                </div>
              </Card>
              <Card className="!p-3.5 space-y-2 text-sm text-slate-600">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-slate-500">
                    Score
                  </p>
                  <span
                    className={`text-xs font-bold tabular-nums ${
                      score.pct >= 75
                        ? 'text-emerald-700'
                        : score.pct >= 40
                          ? 'text-amber-700'
                          : 'text-slate-400'
                    }`}
                  >
                    {score.done}/{score.total} · {score.pct}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      score.pct >= 75
                        ? 'bg-emerald-500'
                        : score.pct >= 40
                          ? 'bg-amber-400'
                          : 'bg-slate-300'
                    }`}
                    style={{ width: `${score.pct}%` }}
                  />
                </div>
                <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
                  {checks.map((c) => (
                    <div
                      key={c.id}
                      className={`flex items-start gap-2 text-xs ${c.ok ? 'text-emerald-700' : 'text-slate-400'}`}
                      title={c.tip}
                    >
                      <span
                        className={`mt-1 h-2 w-2 shrink-0 rounded-full ${c.ok ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      />
                      <span>{c.label}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </aside>
      </form>
    </div>
  )
}
